import { useId, useRef } from "react";
import type { KeyboardEvent } from "react";
import { cn } from "../../lib/cn";
import type { UiDensity } from "../../hooks/useDensity";

export interface DensityToggleProps {
  value: UiDensity;
  onChange: (value: UiDensity) => void;
  disabled?: boolean;
  className?: string;
  label?: string;
}

const OPTIONS: ReadonlyArray<{ value: UiDensity; label: string }> = [
  { value: "comfortable", label: "Confortável" },
  { value: "compact", label: "Compacto" },
];

/**
 * Confortável / Compacto (I3). É uma ESCOLHA, não uma navegação — por isso `radiogroup`
 * (setas trocam, Tab entra e sai do grupo) e não `tablist`. Visual de segmented, raio pill.
 */
export function DensityToggle({
  value,
  onChange,
  disabled = false,
  className,
  label = "Layout",
}: DensityToggleProps) {
  const labelId = useId();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    const next = (index + 1) % OPTIONS.length;
    const option = OPTIONS[next];
    if (!option) return;
    onChange(option.value);
    refs.current[next]?.focus();
  };

  return (
    <div className={cn("flex items-center justify-between gap-3", className)}>
      <span id={labelId} className="text-sm text-muted-foreground">
        {label}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        className="inline-flex rounded-pill bg-muted p-0.5"
      >
        {OPTIONS.map((option, index) => {
          const checked = option.value === value;
          return (
            <button
              key={option.value}
              ref={(node) => {
                refs.current[index] = node;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cn(
                "inline-flex min-h-8 items-center rounded-pill px-3 text-xs font-medium max-md:min-h-11",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
                checked
                  ? "bg-surface text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
